/**
 * Product page STOQ (Restock Rocket) helpers:
 * - Sticky bar title + variant: marquee on hover when text overflows, reset when pointer leaves
 * - Main buy-buttons bridge so preorder UI appears outside the sticky bar
 */
(function () {
  const MOBILE_QUERY = '(max-width: 749px)';
  const MARQUEE_TRACK_CLASS = 'sticky-marquee-track';
  const MARQUEE_SEGMENT_CLASS = 'sticky-marquee-segment';

  /** @type {boolean} */
  let isUpdating = false;

  /** @type {number | undefined} */
  let debounceTimer;

  /** @type {MutationObserver | null} */
  let infoObserver = null;

  /** @type {HTMLElement | null} */
  let observedInfo = null;

  /**
   * @param {HTMLElement} element
   * @param {string} text
   */
  function measureTextWidth(element, text) {
    const probe = document.createElement('span');
    probe.textContent = text;
    probe.style.cssText = [
      'position:absolute',
      'visibility:hidden',
      'white-space:nowrap',
      'pointer-events:none',
      'top:0',
      'left:-9999px',
      `font:${getComputedStyle(element).font}`,
    ].join(';');
    document.body.appendChild(probe);
    const width = probe.getBoundingClientRect().width;
    probe.remove();
    return width;
  }

  /**
   * @param {HTMLElement} element
   * @returns {string}
   */
  function getMarqueeText(element) {
    const track = element.querySelector('.' + MARQUEE_TRACK_CLASS);
    const fromTrack = track?.dataset.originalText?.trim();
    if (fromTrack) return fromTrack;
    return element.textContent.trim();
  }

  /**
   * @param {HTMLElement} element
   * @param {string} text
   */
  function isTextOverflowing(element, text) {
    const available = element.clientWidth;
    if (available <= 0) return false;
    return measureTextWidth(element, text) > available + 1;
  }

  /**
   * @param {HTMLElement} element
   * @param {string} text
   */
  function renderPlainMarquee(element, text) {
    element.classList.remove('is-marquee', 'is-marquee-hover');
    element.style.removeProperty('--marquee-duration');
    element.removeAttribute('title');
    element.replaceChildren(document.createTextNode(text));
  }

  /**
   * @param {HTMLElement} element
   * @param {string} text
   * @param {{ hoverOnly?: boolean }} options
   */
  function renderMarquee(element, text, options = {}) {
    const hoverOnly = Boolean(options.hoverOnly);

    if (
      element.classList.contains('is-marquee') &&
      element.classList.contains('is-marquee-hover') === hoverOnly &&
      element.querySelector('.' + MARQUEE_TRACK_CLASS)?.dataset.originalText === text
    ) {
      return;
    }

    element.classList.add('is-marquee');
    element.classList.toggle('is-marquee-hover', hoverOnly);
    element.title = hoverOnly ? text : '';

    const track = document.createElement('span');
    track.className = MARQUEE_TRACK_CLASS;
    track.dataset.originalText = text;

    const first = document.createElement('span');
    first.className = MARQUEE_SEGMENT_CLASS;
    first.textContent = text;

    const second = document.createElement('span');
    second.className = MARQUEE_SEGMENT_CLASS;
    second.setAttribute('aria-hidden', 'true');
    second.textContent = text;

    track.append(first, second);
    element.replaceChildren(track);

    const duration = Math.min(24, Math.max(8, text.length * 0.35));
    element.style.setProperty('--marquee-duration', `${duration}s`);
  }

  /**
   * @param {HTMLElement} element
   * @param {{ hoverOnly?: boolean }} options
   */
  function setupMarquee(element, options = {}) {
    const text = getMarqueeText(element);
    if (!text) {
      renderPlainMarquee(element, '');
      return;
    }

    if (isTextOverflowing(element, text)) {
      renderMarquee(element, text, options);
      return;
    }

    if (element.classList.contains('is-marquee')) {
      renderPlainMarquee(element, text);
    }
  }

  function initStickyMarquees() {
    if (isUpdating) return;

    isUpdating = true;
    try {
      document.querySelectorAll('.sticky-add-to-cart__title').forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (!node.closest('.sticky-add-to-cart__bar')) return;
        setupMarquee(node, { hoverOnly: true });
      });

      document.querySelectorAll('.sticky-add-to-cart__variant').forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (!node.closest('.sticky-add-to-cart__bar')) return;
        setupMarquee(node, { hoverOnly: true });
      });
    } finally {
      isUpdating = false;
    }
  }

  function scheduleInit() {
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => {
      window.requestAnimationFrame(initStickyMarquees);
    }, 80);
  }

  function scheduleInitWithRetries() {
    scheduleInit();
    window.setTimeout(scheduleInit, 200);
    window.setTimeout(scheduleInit, 600);
  }

  /**
   * @param {HTMLElement} infoEl
   */
  function observeInfoBlock(infoEl) {
    if (observedInfo === infoEl && infoObserver) return;

    infoObserver?.disconnect();
    observedInfo = infoEl;

    infoObserver = new MutationObserver((records) => {
      if (isUpdating) return;

      const fromOurTrack = records.every(
        (record) =>
          record.target instanceof Element &&
          (record.target.classList.contains(MARQUEE_TRACK_CLASS) ||
            record.target.classList.contains(MARQUEE_SEGMENT_CLASS) ||
            record.target.closest('.' + MARQUEE_TRACK_CLASS))
      );

      if (fromOurTrack) return;
      scheduleInit();
    });

    infoObserver.observe(infoEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });
  }

  function bindInfoObserver() {
    const sticky = document.querySelector('sticky-add-to-cart');
    const info = sticky?.querySelector('.sticky-add-to-cart__info');
    if (info instanceof HTMLElement) {
      observeInfoBlock(info);
    }
  }

  function observeStickyBar() {
    const sticky = document.querySelector('sticky-add-to-cart');
    if (!sticky) return;

    bindInfoObserver();

    new MutationObserver((records) => {
      if (isUpdating) return;

      const infoReplaced = records.some(
        (record) =>
          record.type === 'childList' &&
          [...record.addedNodes, ...record.removedNodes].some(
            (node) =>
              node instanceof Element &&
              (node.matches('.sticky-add-to-cart__info') ||
                node.matches('.sticky-add-to-cart__title') ||
                node.matches('.sticky-add-to-cart__variant') ||
                node.querySelector?.('.sticky-add-to-cart__title, .sticky-add-to-cart__variant'))
          )
      );

      const stuckChanged = records.some(
        (record) =>
          record.type === 'attributes' &&
          record.target instanceof HTMLElement &&
          record.attributeName === 'data-stuck'
      );

      if (infoReplaced || stuckChanged) {
        bindInfoObserver();
        scheduleInitWithRetries();
      }
    }).observe(sticky, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-stuck'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      scheduleInitWithRetries();
      observeStickyBar();
    });
  } else {
    scheduleInitWithRetries();
    observeStickyBar();
  }

  window.addEventListener('resize', scheduleInitWithRetries, { passive: true });
  window.matchMedia(MOBILE_QUERY).addEventListener('change', scheduleInitWithRetries);
  window.addEventListener('orientationchange', scheduleInitWithRetries, { passive: true });
  window.addEventListener('stoq:sticky-layout', scheduleInitWithRetries);
})();

/**
 * Mirror STOQ preorder UI from sticky bar → main buy-buttons.
 * Runs after page load so STOQ app scripts inject first. Never touches sticky bar.
 */
(function () {
  const VARIANT_UPDATE = 'variant:update';
  const BRIDGE_MARKER = 'data-stoq-theme-bridge';
  const MAIN_PREORDER_CLASS = 'stoq-theme-preorder-button';
  const MAIN_SHIPPING_CLASS = 'stoq-theme-preorder-shipping';
  const STICKY_ACTION_CLASS = 'sticky-add-to-cart__preorder-action';

  /** @type {number | undefined} */
  let syncTimer;

  /**
   * @param {HTMLElement} button
   * @returns {string}
   */
  function getButtonLabelText(button) {
    const label =
      button.querySelector('.add-to-cart-text__content span span') ||
      button.querySelector('.add-to-cart-text__content span') ||
      button.querySelector('.add-to-cart-text__content');

    return (label?.textContent || button.textContent || '').trim();
  }

  /**
   * @param {HTMLElement} button
   * @param {string} text
   */
  function updateButtonLabel(button, text) {
    const label =
      button.querySelector('.add-to-cart-text__content span span') ||
      button.querySelector('.add-to-cart-text__content span') ||
      button.querySelector('.add-to-cart-text__content');

    if (label) label.textContent = text;
  }

  function clearMainBridge() {
    const container = document.querySelector('.product-form-buttons');
    if (!(container instanceof HTMLElement)) return;

    container
      .querySelectorAll('[' + BRIDGE_MARKER + '="description"], .' + MAIN_SHIPPING_CLASS)
      .forEach((node) => node.remove());

    const button = container.querySelector('.add-to-cart-button[' + BRIDGE_MARKER + '="button"]');
    if (button instanceof HTMLElement) {
      button.classList.remove(MAIN_PREORDER_CLASS, 'restock-rocket-preorder-button');
      button.removeAttribute(BRIDGE_MARKER);
    }
  }

  /**
   * @param {HTMLElement} mainContainer
   * @param {HTMLElement} mainButton
   * @param {string} text
   */
  function placeMainShippingDescription(mainContainer, mainButton, text) {
    mainContainer
      .querySelectorAll('[' + BRIDGE_MARKER + '="description"], .' + MAIN_SHIPPING_CLASS)
      .forEach((node) => node.remove());

    const description = document.createElement('div');
    description.className = MAIN_SHIPPING_CLASS;
    description.setAttribute(BRIDGE_MARKER, 'description');
    description.textContent = text;

    mainButton.insertAdjacentElement('afterend', description);
  }

  /**
   * @param {HTMLElement} mainButton
   * @param {string} buttonText
   */
  function applyMainPreorderButton(mainButton, buttonText) {
    mainButton.classList.add(MAIN_PREORDER_CLASS);
    mainButton.setAttribute(BRIDGE_MARKER, 'button');
    mainButton.disabled = false;
    mainButton.removeAttribute('hidden');
    updateButtonLabel(mainButton, buttonText);
  }

  /**
   * @param {ParentNode | null | undefined} root
   * @returns {boolean}
   */
  function hasNativeStoq(root) {
    if (!root) return false;
    return Boolean(root.querySelector('[class*="restock-rocket"]:not([' + BRIDGE_MARKER + '])'));
  }

  /**
   * @param {string} text
   * @returns {boolean}
   */
  function isPreorderButtonLabel(text) {
    if (!text) return false;
    if (/sold out|unavailable|add to cart|加入购物车/i.test(text)) return false;
    return /pre\s*order|预购/i.test(text);
  }

  /**
   * @returns {boolean}
   */
  function stickyHasPreorder() {
    const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
    if (!(stickyBar instanceof HTMLElement)) return false;

    if (hasNativeStoq(stickyBar)) return true;
    if (stickyBar.querySelector('[' + BRIDGE_MARKER + '="sticky-shipping"]')) return true;

    const stickyButton = stickyBar.querySelector('.sticky-add-to-cart__button');
    if (!(stickyButton instanceof HTMLElement)) return false;

    if (stickyButton.classList.contains('restock-rocket-preorder-button')) return true;
    return isPreorderButtonLabel(getButtonLabelText(stickyButton));
  }

  /**
   * @returns {boolean}
   */
  function mainHasNativePreorderButton() {
    const mainContainer = document.querySelector('.product-form-buttons');
    if (!mainContainer) return false;

    return Boolean(
      mainContainer.querySelector(
        '.restock-rocket-preorder-button:not([' +
          BRIDGE_MARKER +
          ']), [ref="addToCartButton"][class*="restock-rocket"]:not([' +
          BRIDGE_MARKER +
          '])'
      )
    );
  }

  /**
   * @returns {string}
   */
  function resolveShippingText() {
    const variantInput = document.querySelector(
      '.product-form-buttons input[name="id"], product-form-component input[name="id"]'
    );
    const variantId = variantInput instanceof HTMLInputElement ? variantInput.value : null;
    const plan = variantId ? window._RestockRocket?.getSellingPlan?.(String(variantId)) : null;

    if (plan?.enabled && plan.preorder_button_description_show_shipping !== false) {
      const prefix = String(plan.preorder_button_description_shipping_text_prefix || 'Shipping: ');
      let shipping = String(plan.preorder_shipping_text || plan.shipping_text || '');
      shipping = shipping.replace(/\{\{[^}]+\}\}/g, '').trim();
      return prefix + (shipping || 'As soon as possible');
    }

    const nativeStickyDesc = document.querySelector(
      '.sticky-add-to-cart__bar .restock-rocket-preorder-description:not([' + BRIDGE_MARKER + '])'
    );
    if (nativeStickyDesc?.textContent?.trim()) {
      return nativeStickyDesc.textContent.trim();
    }

    const mainDesc = document.querySelector(
      '.product-form-buttons .' + MAIN_SHIPPING_CLASS + ', .product-form-buttons [' + BRIDGE_MARKER + '="description"]'
    );
    if (mainDesc?.textContent?.trim()) {
      return mainDesc.textContent.trim();
    }

    return 'Shipping: As soon as possible';
  }

  /**
   * @param {HTMLElement} element
   */
  function normalizeStickyShippingElement(element) {
    element.style.removeProperty('background-color');
    element.style.removeProperty('background');
    element.style.removeProperty('border-radius');
    element.style.removeProperty('border');
    element.style.removeProperty('padding-block');
    element.style.removeProperty('padding-inline');
    element.style.removeProperty('color');

    element.querySelectorAll('.preorder-description-detail, .preorder-detail-item').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      node.style.removeProperty('color');
      node.style.removeProperty('background-color');
      node.style.removeProperty('background');
    });
  }

  /**
   * STOQ sometimes only renames the sticky button to Preorder without inserting
   * the shipping line. Restore blue text below the button when missing.
   */
  function ensureStickyShippingDescription() {
    const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
    if (!(stickyBar instanceof HTMLElement)) return;

    const stickyButton = stickyBar.querySelector('.sticky-add-to-cart__button');
    const hasPreorderButton =
      stickyButton instanceof HTMLElement &&
      (stickyButton.classList.contains('restock-rocket-preorder-button') ||
        hasNativeStoq(stickyBar) ||
        isPreorderButtonLabel(getButtonLabelText(stickyButton)));

    const bridgeShipping = stickyBar.querySelector('[' + BRIDGE_MARKER + '="sticky-shipping"]');
    const nativeShipping = stickyBar.querySelector(
      '.restock-rocket-preorder-description:not([' + BRIDGE_MARKER + '])'
    );

    if (!hasPreorderButton) {
      bridgeShipping?.remove();
      return;
    }

    if (nativeShipping instanceof HTMLElement) {
      bridgeShipping?.remove();
      normalizeStickyShippingElement(nativeShipping);
      return;
    }

    const shippingText = resolveShippingText();

    if (bridgeShipping instanceof HTMLElement) {
      bridgeShipping.textContent = shippingText;
      return;
    }

    const description = document.createElement('div');
    description.className = 'restock-rocket-preorder-description';
    description.setAttribute(BRIDGE_MARKER, 'sticky-shipping');
    description.textContent = shippingText;

    if (stickyButton instanceof HTMLElement && stickyButton.parentElement === stickyBar) {
      stickyButton.insertAdjacentElement('afterend', description);
      return;
    }

    stickyBar.appendChild(description);
  }

  /**
   * @param {HTMLElement} wrapper
   */
  function unwrapStickyPreorderAction(wrapper) {
    const stickyBar = wrapper.parentElement;
    if (!(stickyBar instanceof HTMLElement)) {
      wrapper.remove();
      return;
    }

    const button = wrapper.querySelector('.sticky-add-to-cart__button');
    const shipping = wrapper.querySelector(
      '.restock-rocket-preorder-description, [' + BRIDGE_MARKER + '="sticky-shipping"]'
    );

    if (button instanceof HTMLElement) {
      stickyBar.insertBefore(button, wrapper);
    }
    if (shipping instanceof HTMLElement) {
      stickyBar.insertBefore(shipping, wrapper);
    }

    wrapper.remove();
  }

  /** Keep original sticky bar flex row; stack button + shipping in one column. */
  function ensureStickyPreorderAction() {
    const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
    if (!(stickyBar instanceof HTMLElement)) return;

    const existingWrapper = stickyBar.querySelector('[' + BRIDGE_MARKER + '="sticky-action"]');

    if (!stickyHasPreorder()) {
      if (existingWrapper instanceof HTMLElement) {
        unwrapStickyPreorderAction(existingWrapper);
      }
      return;
    }

    const stickyButton = stickyBar.querySelector(
      '.sticky-add-to-cart__button.restock-rocket-preorder-button, .sticky-add-to-cart__button[class*="restock-rocket"]'
    );
    const button =
      stickyButton instanceof HTMLElement
        ? stickyButton
        : stickyBar.querySelector('.sticky-add-to-cart__button');

    if (!(button instanceof HTMLElement)) return;

    const shipping =
      stickyBar.querySelector('.restock-rocket-preorder-description:not([' + BRIDGE_MARKER + '])') ||
      stickyBar.querySelector('[' + BRIDGE_MARKER + '="sticky-shipping"]');

    let wrapper = existingWrapper;
    if (!(wrapper instanceof HTMLElement)) {
      wrapper = document.createElement('div');
      wrapper.className = STICKY_ACTION_CLASS;
      wrapper.setAttribute(BRIDGE_MARKER, 'sticky-action');
      stickyBar.insertBefore(wrapper, button);
    }

    if (button.parentElement !== wrapper) {
      wrapper.appendChild(button);
    }

    if (shipping instanceof HTMLElement && shipping.parentElement !== wrapper) {
      wrapper.appendChild(shipping);
    } else if (shipping instanceof HTMLElement && button.nextElementSibling !== shipping) {
      wrapper.appendChild(shipping);
    }
  }

  /**
   * @returns {boolean}
   */
  function applyMainPreorderFromStickyContext() {
    const stickyBar = document.querySelector('.sticky-add-to-cart__bar');
    const mainContainer = document.querySelector('.product-form-buttons');
    const mainButton = mainContainer?.querySelector('[ref="addToCartButton"], .add-to-cart-button');

    if (!(stickyBar instanceof HTMLElement) || !(mainContainer instanceof HTMLElement)) return false;
    if (!(mainButton instanceof HTMLElement)) return false;
    if (!stickyHasPreorder()) return false;

    if (mainHasNativePreorderButton()) {
      clearMainBridge();
      return true;
    }

    const stickyButton = stickyBar.querySelector('.sticky-add-to-cart__button');
    const stickyDescription =
      stickyBar.querySelector('.restock-rocket-preorder-description:not([' + BRIDGE_MARKER + '])') ||
      stickyBar.querySelector('[' + BRIDGE_MARKER + '="sticky-shipping"]');

    let buttonText = 'Preorder';
    if (stickyButton instanceof HTMLElement) {
      const label = getButtonLabelText(stickyButton);
      if (label && !/sold out|unavailable/i.test(label)) {
        buttonText = label;
      }
    }

    clearMainBridge();

    applyMainPreorderButton(mainButton, buttonText);

    const shippingText = stickyDescription?.textContent?.trim() || resolveShippingText();
    if (shippingText) {
      placeMainShippingDescription(mainContainer, mainButton, shippingText);
    }

    return true;
  }

  /**
   * Fallback when STOQ API exists but sticky DOM is not ready yet.
   */
  function syncMainFromSellingPlan() {
    const variantInput = document.querySelector(
      '.product-form-buttons input[name="id"], product-form-component input[name="id"]'
    );
    const mainContainer = document.querySelector('.product-form-buttons');
    const mainButton = mainContainer?.querySelector('[ref="addToCartButton"], .add-to-cart-button');

    if (!(variantInput instanceof HTMLInputElement) || !variantInput.value) return false;
    if (!(mainContainer instanceof HTMLElement) || !(mainButton instanceof HTMLElement)) return false;
    if (mainHasNativePreorderButton() || applyMainPreorderFromStickyContext()) return true;

    const plan = window._RestockRocket?.getSellingPlan?.(String(variantInput.value));
    if (!plan?.enabled) return false;

    clearMainBridge();

    applyMainPreorderButton(mainButton, String(plan.preorder_button_text || 'Preorder'));

    const prefix = String(plan.preorder_button_description_shipping_text_prefix || 'Shipping: ');
    let shipping = String(plan.preorder_shipping_text || plan.shipping_text || '');
    shipping = shipping.replace(/\{\{[^}]+\}\}/g, '').trim();
    if (shipping && plan.preorder_button_description_show_shipping !== false) {
      placeMainShippingDescription(mainContainer, mainButton, prefix + (shipping || 'As soon as possible'));
    }

    return true;
  }

  function runSync() {
    ensureStickyShippingDescription();
    ensureStickyPreorderAction();
    window.dispatchEvent(new CustomEvent('stoq:sticky-layout'));
    if (applyMainPreorderFromStickyContext()) return;
    syncMainFromSellingPlan();
  }

  function scheduleSync() {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(runSync, 120);
  }

  function scheduleSyncBurst() {
    scheduleSync();
    window.setTimeout(runSync, 400);
    window.setTimeout(runSync, 1000);
    window.setTimeout(runSync, 2500);
  }

  function initStoqBridge() {
    if (!document.querySelector('.product-form-buttons')) return;

    runSync();
    scheduleSyncBurst();

    document.addEventListener(VARIANT_UPDATE, () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(scheduleSyncBurst);
      });
    });

    const sticky = document.querySelector('sticky-add-to-cart');
    if (sticky) {
      new MutationObserver(scheduleSync).observe(sticky, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'disabled'],
        characterData: true,
      });
    }

    const mainButtons = document.querySelector('.product-form-buttons');
    if (mainButtons) {
      new MutationObserver(scheduleSync).observe(mainButtons, {
        childList: true,
        subtree: true,
      });
    }
  }

  if (document.readyState === 'complete') {
    initStoqBridge();
  } else {
    window.addEventListener('load', initStoqBridge, { once: true });
  }
})();
